import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { WorkProductType } from '../api'

// The two pull type tags. Slugs match the backend whitelist
// (WORK_PRODUCT_TYPES); labels are the modal's. Single source of truth for
// BOTH the email Pull (pane-3 header) and the markup Pull (markup modal), so
// the two never drift. Ken 2026-08-11; shared 2026-08-14.
export const PULL_TYPES: { slug: WorkProductType; label: string }[] = [
  { slug: 'advice', label: 'Advice given' },
  { slug: 'language', label: 'Language drafted' },
]

type Props = {
  /** Selected type slugs (multi-select). */
  types: Set<WorkProductType>
  onToggleType: (slug: WorkProductType) => void
  /** Markup-only: where in the document this redline/comment sits (derived
   *  paragraph/section location, or the quoted anchor). Renders a labeled
   *  WHERE row above the passage; omitted (email context) → no row. */
  where?: string | null
  /** Passage-preview content, rendered inside the standard 2px rail. */
  children: ReactNode
  onCancel: () => void
  /** Called with the user's Notes text (carried to Playbook). */
  onSave: (note: string) => void
  busy: boolean
}

/**
 * PullDialog — the shared "pull as work product" modal (Ken 2026-08-11 design;
 * extracted 2026-08-14 so the email Pull and the markup Pull ARE the same
 * dialog). Layout: eyebrow · multi-select type buttons · optional WHERE row
 * (markup) · passage rail · Cancel/Pull. Enter saves, Esc cancels; the first
 * type button autofocuses on open.
 */
export default function PullDialog({
  types, onToggleType, where, children, onCancel, onSave, busy,
}: Props) {
  const firstTypeRef = useRef<HTMLButtonElement>(null)
  const [note, setNote] = useState('')
  useEffect(() => { firstTypeRef.current?.focus() }, [])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Pull as work product"
      // stopPropagation so a backdrop click can't bubble to a parent
      // backdrop-closing modal (the markup Pull renders inside one).
      onClick={(e) => { e.stopPropagation(); onCancel() }}
      className="fixed inset-0 z-[80] flex items-center justify-center p-6"
      style={{ background: 'rgba(0,0,0,0.55)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); onCancel() }
          // Enter saves. Intercepted at the dialog level so it fires
          // regardless of focused control and doesn't merely toggle a
          // focused type button. (The Notes textarea stops Enter from
          // reaching here so it inserts a newline instead.)
          else if (e.key === 'Enter') { e.preventDefault(); onSave(note) }
        }}
        className="me-chrome w-[480px] max-w-full rounded-lg bg-[color:var(--me-surface)] p-5 shadow-2xl"
        style={{ border: '1px solid var(--me-border)' }}
      >
        {/* 1 · Eyebrow */}
        <div className="text-[11px] uppercase tracking-[0.14em] me-text-mute">Pull</div>

        {/* 2 · Type buttons — the most prominent element. Multi-select. */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          {PULL_TYPES.map((opt, i) => {
            const on = types.has(opt.slug)
            return (
              <button
                key={opt.slug}
                ref={i === 0 ? firstTypeRef : undefined}
                type="button"
                aria-pressed={on}
                onClick={() => onToggleType(opt.slug)}
                className="rounded-md text-[14px] transition-colors"
                style={on
                  ? { padding: '11px 8px', background: '#F5E6D3', border: '1.5px solid #BA7517', color: '#854F0B' }
                  : { padding: '11px 8px', background: 'transparent', border: '1.5px solid var(--me-border)', color: 'var(--me-textDim)' }}
              >
                {opt.label}
              </button>
            )
          })}
        </div>

        {/* 3 · WHERE (markup only) — the document location this pull came from.
            Labeled like the eyebrow; omitted entirely in the email context. */}
        {where ? (
          <div className="mt-4 flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-[0.14em] me-text-mute shrink-0">Where</span>
            <span className="text-[12px] me-text-dim me-mono break-words min-w-0">{where}</span>
          </div>
        ) : null}

        {/* 4 · Passage preview. 2px rail, 10px indent. */}
        <div className="mt-4" style={{ borderLeft: '2px solid var(--me-border)', paddingLeft: 10 }}>
          {children}
        </div>

        {/* 5 · Notes — free text, carried through to Playbook. No placeholder
            (Emerson 2026-08-14 — an empty box, not a prompt). */}
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.14em] me-text-mute mb-1.5">Notes</div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            // Enter = newline here; stop it bubbling to the dialog's Enter-saves.
            onKeyDown={(e) => { if (e.key === 'Enter') e.stopPropagation() }}
            rows={2}
            className="me-input w-full text-[12px]"
          />
        </div>

        {/* 6 · Actions — right-aligned, no icons. */}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md text-[12px]"
            style={{ padding: '6px 14px', background: 'transparent', border: '1.5px solid var(--me-border)', color: 'var(--me-textDim)' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(note)}
            disabled={busy}
            className="rounded-md text-[12px] disabled:opacity-60"
            style={{ padding: '6px 14px', background: '#BA7517', color: '#fff', border: '1.5px solid #BA7517' }}
          >
            Pull
          </button>
        </div>
      </div>
    </div>
  )
}
